import { useEffect, useState } from 'react';
import api from '../lib/api';

// Global cache for SEO settings
let seoCache = null;
let seoPromise = null;

export const useSEO = (pageTitle = '', additionalKeywords = [], customImage = '', customDescription = '') => {
  const [seoSettings, setSeoSettings] = useState(seoCache);

  useEffect(() => {
    const loadSeoSettings = async () => {
      // Use cache if available
      if (seoCache) {
        setSeoSettings(seoCache);
        return;
      }

      // Use existing promise if loading
      if (seoPromise) {
        const data = await seoPromise;
        setSeoSettings(data);
        return;
      }

      // Load SEO settings
      seoPromise = api.get('/settings/seo')
        .then(res => {
          seoCache = res.data;
          return res.data;
        })
        .catch(() => {
          seoCache = { seo_site_title: 'SIOS', seo_site_image: '', seo_keywords: [] };
          return seoCache;
        });

      const data = await seoPromise;
      setSeoSettings(data);
    };

    loadSeoSettings();
  }, []);

  useEffect(() => {
    if (!seoSettings) return;

    // Update document title
    const siteTitle = seoSettings.seo_site_title || 'SIOS';
    // Se pageTitle è vuoto, 'Home' o non significativo, usa solo il titolo SEO dai settings
    const isGenericTitle = !pageTitle || pageTitle.toLowerCase() === 'home';
    document.title = isGenericTitle ? siteTitle : `${siteTitle} - ${pageTitle}`;

    // Combine keywords
    const allKeywords = [
      ...(seoSettings.seo_keywords || []),
      ...additionalKeywords
    ].filter(Boolean);

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, attribute, content) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (selector.includes('property=')) {
          const prop = selector.match(/property="([^"]+)"/)?.[1];
          if (prop) meta.setAttribute('property', prop);
        } else if (selector.includes('name=')) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) meta.name = name;
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update meta keywords
    updateMetaTag('meta[name="keywords"]', 'content', allKeywords.join(', '));

    // Update description
    const description = customDescription || `${siteTitle} - L'evento più importante per l'innovazione e le startup in Italia`;
    updateMetaTag('meta[name="description"]', 'content', description);

    // Update og:title
    updateMetaTag('meta[property="og:title"]', 'content', document.title);

    // Update og:description
    updateMetaTag('meta[property="og:description"]', 'content', description);

    // Update og:url
    updateMetaTag('meta[property="og:url"]', 'content', window.location.href);

    // Update og:image
    const imageUrl = customImage || seoSettings.seo_site_image || '';
    if (imageUrl) {
      const fullImageUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      updateMetaTag('meta[property="og:image"]', 'content', fullImageUrl);
    }

    // Twitter Card
    updateMetaTag('meta[name="twitter:title"]', 'content', document.title);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);
    if (imageUrl) {
      const fullImageUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      updateMetaTag('meta[name="twitter:image"]', 'content', fullImageUrl);
    }

  }, [seoSettings, pageTitle, additionalKeywords, customImage, customDescription]);

  return seoSettings;
};

// Clear cache (useful for admin after saving settings)
export const clearSEOCache = () => {
  seoCache = null;
  seoPromise = null;
};
